<script lang="ts">
	import { FileUploader } from "@file-upload/client"
	import { DataHandlerMedia } from "luxedo-data"
	import { LuxedoRPC } from "luxedo-rpc"
	import { onMount } from "svelte"
	import { Toast } from "toaster"
	import { mediaProgressHandler } from "../MediaProgressHandler.svelte"

	interface Props {
		entryId?: number
		folderId?: number
		userId?: number
		onComplete: () => void
		onStart: () => void
	}

	let { entryId, folderId, userId, onComplete, onStart }: Props = $props()

	let initialMedia: Array<number> = []
	const processedMedia = new Set<number>()

	const ALLOWED_MIME_TYPES = [
		"image/png",
		"image/jpeg",
		"image/jpg",
		"image/webp",
		"video/mp4",
		"video/quicktime",
		// "video/webm", removed after testing - doesn't seem to work
		"image/gif",
		"audio/mpeg",
		"audio/mp3",
		"audio/wav",
	]

	async function getUserMediaList() {
		const res = await LuxedoRPC.api.media.get_user_media()
		return res.media
	}

	async function getNewMediaIds(): Promise<Array<number>> {
		console.log("Checking for new IDs", initialMedia)

		// try for 15 seconds
		for (let i = 0; i < 15; i++) {
			const userMedia = await getUserMediaList()
			const newIds = userMedia.filter((m) => !initialMedia.includes(m))

			console.log("Found", newIds)
			if (newIds.length) return newIds

			await new Promise((res) => setTimeout(res, 1000)) // refresh every second until new media is found
		}

		return []
	}

	async function onUploadSuccess() {
		if (entryId) await DataHandlerMedia.pull([entryId])
		else {
			const newMediaIds = await getNewMediaIds()
			initialMedia = initialMedia.concat(newMediaIds)
			await DataHandlerMedia.pull(newMediaIds)
			const media = DataHandlerMedia.getMany(newMediaIds)
			for (const med of media) {
				if (processedMedia.has(med.id)) continue
				processedMedia.add(med.id)
				await LuxedoRPC.api.media.media_process_begin(med.id)
				mediaProgressHandler.setTemporaryProgress(med.id, 0.01)
			}
		}
	}

	function handleSuccess() {
		Toast.success("Upload(s) complete! Processing now...")
		onComplete()
	}

	const DEFAULT_ERROR_MESSAGE = "Upload(s) failed... Please refresh and try again."
	let errorMessage: string = DEFAULT_ERROR_MESSAGE
	function handleFail() {
		Toast.error(errorMessage)
	}

	function handleMixed(successAmount: number, totalAmount: number) {
		Toast.text(`${successAmount} of ${totalAmount} uploaded successfully! Processing now.`)
		onComplete()
	}

	onMount(async () => {
		initialMedia = await getUserMediaList()
	})
</script>

<FileUploader
	endpoint={import.meta.env.VITE_UPLOAD_URL}
	allowedMimeTypes={ALLOWED_MIME_TYPES}
	uploadType={"media"}
	{entryId}
	{folderId}
	{userId}
	onStart={() => {
		errorMessage = DEFAULT_ERROR_MESSAGE
	}}
	onUploadComplete={onUploadSuccess}
	onError={(e) => {
		console.log(e, e.code)
		if (e.code === 507) errorMessage = "Not enough storage."
		else if (e.code === 415) errorMessage = "Invalid file type."
		else errorMessage = "Unable to begin upload."
	}}
	onAllComplete={(e) => {
		if (e.failed.length && e.successful.length)
			return handleMixed(e.successful.length, e.successful.length + e.failed.length)
		else if (e.failed.length) return handleFail()
		else if (e.successful.length) return handleSuccess()
	}}
/>

<style>
</style>
